import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { C, S } from './cedsStyles';

const CATEGORY_ORDER = ['ceds_draft', 'charter', 'reference', 'other'];
const CATEGORY_LABELS = {
  ceds_draft: 'CEDS Draft Documents',
  charter:    'Council Charter',
  reference:  'Reference Materials',
  other:      'Additional Documents',
};

function fmt(kb) {
  if (!kb) return '';
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DocumentLibraryPage() {
  const [docs, setDocs]         = useState([]);
  const [member, setMember]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [downloadedId, setDownloadedId] = useState(null);

  // Upload form state
  const [uTitle, setUTitle]       = useState('');
  const [uDesc, setUDesc]         = useState('');
  const [uCat, setUCat]           = useState('ceds_draft');
  const [uMeetNum, setUMeetNum]   = useState('');
  const [uFile, setUFile]         = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');

  const fetchDocs = useCallback(async () => {
    const { data } = await supabase
      .from('ceds_documents')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    setDocs(data || []);
  }, []);

  useEffect(() => {
    async function init() {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: m } = await supabase
          .from('ceds_council_members')
          .select('role')
          .eq('id', session.user.id)
          .maybeSingle();
        setMember(m);
      }
      await fetchDocs();
      setLoading(false);
    }
    init();
  }, [fetchDocs]);

  async function handleDownload(doc) {
    setDownloading(doc.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/get-download-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ filePath: doc.file_path, documentId: doc.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.signedUrl) {
        const a = document.createElement('a');
        a.href = body.signedUrl;
        a.download = doc.file_name || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setDownloadedId(doc.id);
        setTimeout(() => setDownloadedId(null), 3000);
      } else {
        alert(`Download failed: ${body.error ?? 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Download error: ${err.message}`);
    }
    setDownloading(null);
  }

  async function handleDeactivate(docId) {
    await supabase.from('ceds_documents').update({ is_active: false }).eq('id', docId);
    await fetchDocs();
  }

  async function handleUpload() {
    setUploadErr('');
    if (!uTitle.trim()) { setUploadErr('Title is required.'); return; }
    if (!uFile)         { setUploadErr('Please select a file.'); return; }

    setUploading(true);
    try {
      const safeName = uFile.name.replace(/\s+/g, '_');
      const filePath = `${uCat}/${Date.now()}-${safeName}`;

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setUploadErr('Your session has expired. Please sign out and back in.'); return; }

      const tokenRes = await fetch('/api/get-upload-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ filePath }),
      });
      if (!tokenRes.ok) {
        const body = await tokenRes.json().catch(() => ({}));
        setUploadErr(`Upload error: ${body.error ?? tokenRes.status}`);
        return;
      }
      const { token } = await tokenRes.json();

      const { error: storageErr } = await supabase.storage
        .from('ceds-docs')
        .uploadToSignedUrl(filePath, token, uFile);
      if (storageErr) {
        setUploadErr(`Storage error: ${storageErr.message}`);
        return;
      }

      const { error: dbErr } = await supabase.from('ceds_documents').insert({
        title:        uTitle.trim(),
        description:  uDesc.trim() || null,
        category:     uCat,
        meeting_num:  uCat === 'meeting_packet' ? parseInt(uMeetNum, 10) : null,
        file_path:    filePath,
        file_name:    uFile.name,
        file_size_kb: Math.round(uFile.size / 1024),
        uploaded_by:  session.user.id,
      });
      if (dbErr) {
        setUploadErr(`Database error: ${dbErr.message} [code: ${dbErr.code}]`);
        return;
      }

      setUTitle(''); setUDesc(''); setUCat('ceds_draft'); setUMeetNum(''); setUFile(null);
      setShowUpload(false);
      await fetchDocs();
    } catch (err) {
      setUploadErr(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }

  // Group docs by category
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = docs.filter(d => d.category === cat);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ color: C.muted, paddingTop: 40, textAlign: 'center' }}>Loading documents…</div>
    );
  }

  const isAdmin = member?.role === 'padd_admin';

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h2 style={{ color: C.navy, margin: '0 0 4px', fontSize: '1.5rem' }}>CEDS Document Library</h2>
          <p style={{ color: C.muted, margin: 0, fontSize: 14 }}>
            All documents for the 2026–2031 PADD Comprehensive Economic Development Strategy.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowUpload(v => !v)}
            style={{ ...S.btn.gold, whiteSpace: 'nowrap', marginLeft: 16 }}
          >
            {showUpload ? 'Cancel' : '+ Upload Document'}
          </button>
        )}
      </div>

      {/* Upload form (admin only, inline) */}
      {isAdmin && showUpload && (
        <div style={{
          ...S.card,
          borderLeft: `4px solid ${C.gold}`,
          marginBottom: 32,
          background: C.cream,
        }}>
          <h3 style={{ color: C.navy, margin: '0 0 16px', fontSize: '1rem' }}>Upload New Document</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={S.label}>Title *</label>
              <input value={uTitle} onChange={e => setUTitle(e.target.value)} style={S.input} placeholder="e.g. 2026–2031 CEDS Draft" />
            </div>
            <div>
              <label style={S.label}>Category *</label>
              <select value={uCat} onChange={e => setUCat(e.target.value)} style={S.input}>
                {CATEGORY_ORDER.map(cat => (
                  <option key={cat} value={cat}>{CATEGORY_LABELS[cat]}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Description</label>
            <textarea value={uDesc} onChange={e => setUDesc(e.target.value)} rows={2}
              style={{ ...S.input, resize: 'vertical' }} placeholder="Optional short description" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>File (PDF, DOC, DOCX) *</label>
            <input type="file" accept=".pdf,.doc,.docx" onChange={e => setUFile(e.target.files[0])}
              style={{ fontSize: 14 }} />
          </div>

          {uploadErr && (
            <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{uploadErr}</div>
          )}

          <button onClick={handleUpload} disabled={uploading} style={{ ...S.btn.primary, opacity: uploading ? 0.65 : 1 }}>
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      )}

      {/* Document sections */}
      {docs.length === 0 && !isAdmin && (
        <div style={{
          ...S.card, textAlign: 'center', color: C.muted,
          padding: '48px 24px', fontSize: 15,
        }}>
          No documents have been uploaded yet. Check back soon.
        </div>
      )}

      {CATEGORY_ORDER.map(cat => {
        const catDocs = grouped[cat];

        if (catDocs.length === 0) return null;

        return (
          <div key={cat} style={{ marginBottom: 32 }}>
            <h3 style={{ color: C.navy, fontSize: '1rem', marginBottom: 12, fontWeight: 700 }}>
              {CATEGORY_LABELS[cat]}
            </h3>
            {catDocs.map(doc => (
              <DocCard key={doc.id} doc={doc} isAdmin={isAdmin}
                onDownload={handleDownload} onDeactivate={handleDeactivate}
                downloading={downloading} downloaded={downloadedId === doc.id} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function DocCard({ doc, isAdmin, onDownload, onDeactivate, downloading, downloaded }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div style={{ ...S.card, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 18 }}>📄</span>
          <span style={{ fontWeight: 600, fontSize: 15, color: C.navy }}>{doc.title}</span>
        </div>
        {doc.description && (
          <p style={{ margin: '0 0 6px 26px', fontSize: 13, color: C.muted }}>{doc.description}</p>
        )}
        <div style={{ marginLeft: 26, fontSize: 12, color: C.muted }}>
          Uploaded {fmtDate(doc.created_at)}
          {doc.file_size_kb ? ` · ${fmt(doc.file_size_kb)}` : ''}
          {' · '}{doc.file_name}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
        <button
          onClick={() => onDownload(doc)}
          disabled={downloading === doc.id || downloaded}
          style={{
            ...S.btn.primary,
            fontSize: 13,
            padding: '7px 16px',
            ...(downloaded && { background: '#2e7d32' }),
          }}
        >
          {downloading === doc.id ? 'Downloading…' : downloaded ? 'Downloaded ✓' : 'Download ↓'}
        </button>

        {isAdmin && !confirming && (
          <button
            onClick={() => setConfirming(true)}
            style={{ ...S.btn.secondary, fontSize: 12, padding: '6px 12px', color: C.muted, borderColor: C.rule }}
          >
            Deactivate
          </button>
        )}
        {isAdmin && confirming && (
          <>
            <span style={{ fontSize: 12, color: C.red }}>Remove?</span>
            <button onClick={() => onDeactivate(doc.id)}
              style={{ ...S.btn.secondary, fontSize: 12, padding: '6px 12px', color: C.red, borderColor: C.red }}>
              Yes
            </button>
            <button onClick={() => setConfirming(false)}
              style={{ ...S.btn.secondary, fontSize: 12, padding: '6px 12px' }}>
              No
            </button>
          </>
        )}
      </div>
    </div>
  );
}
